import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, FieldArray, Form } from 'formik';
import { Box, Button } from '@rsos/components/capstone/base';
import {
  assignPermissionsToRoles,
  selectUserOrgByApp,
  fetchAllPermissions,
  swallowThunkRejection,
} from '@rsos/sinatra';
import {
  checkPremiumPermissions,
  notifyMissingRequiredPermissions,
} from '@rsos/utils/checkPremiumPermissions';
import { notifySuccess, notifyError } from '@rsos/utils/toastUtils';
import AssignDataSourcesDropdown from './AssignDataSourcesDropdown';
import DataSourceCard from './DataSourceCard';
import { Wrapper } from './DataSources.styles';

const DataSourcesForm = ({ dataSources, roles }) => {
  const sinatra = useSelector(state => state.sinatra);
  const dispatch = useDispatch();
  const { application } = sinatra.user.currentRole;
  const org = selectUserOrgByApp(sinatra, application);
  const [isSelectAll, setIsSelectAll] = useState(false);

  const handleAssign = (payloadObj, resetForm, onDropdownClose) => {
    const payload = {
      role_ids: [...payloadObj.role_ids],
      permission_names: [...payloadObj.permission_names],
    };

    const missingRequiredPermissions = checkPremiumPermissions({
      allRoles: roles,
      assignedPermissions: payload.permission_names,
      assignedRoleIds: payload.role_ids,
    });

    if (missingRequiredPermissions) {
      notifyMissingRequiredPermissions();
      onDropdownClose();
      return;
    }

    dispatch(assignPermissionsToRoles(org.id, application, payload))
      .then(() => {
        // when we assign data source to a role, roles get updated, but roles nested in data sources, don't get updated. Hence, we have to fetch.
        // fetchAllPermissions dispatches its own error action on failure; catch the rejection so this refresh cannot leak as an unhandled rejection.
        dispatch(fetchAllPermissions(org.id, application)).catch(
          swallowThunkRejection,
        );
        notifySuccess('Data Source Successfully Assigned');
        resetForm();
        setIsSelectAll(false);
        onDropdownClose();
      })
      .catch(action => {
        const { error } = action;
        notifyError(error.response.data.detail);
      });
  };

  const handleCheckbox = (event, value, checkboxValues, arrayHelpers) => {
    setIsSelectAll(false);
    if (event.target.checked) {
      arrayHelpers.push(value);
    } else {
      arrayHelpers.remove(checkboxValues.indexOf(value));
    }
  };

  const handleSelectAll = setValues => {
    setIsSelectAll(true);
    const names = dataSources.reduce((filtered, ds) => {
      !ds.isDisabled && filtered.push(ds.name);
      return filtered;
    }, []);
    setValues({ dataSources: names });
  };
  const handleDeselectAll = setValues => {
    setIsSelectAll(false);
    setValues({ dataSources: [] });
  };

  return (
    <Formik initialValues={{ dataSources: [] }}>
      {({ values, setValues, resetForm }) => (
        <Form id="dataSourcesForm">
          <FieldArray name="dataSources">
            {arrayHelpers => (
              <div
                id="dataSources-checkbox-group"
                aria-labelledby="dataSources-checkbox-group"
                role="group"
              >
                <Box justify="flex-end" horizontal flow={7} mt={10} px={10}>
                  <Box width={131}>
                    <Button
                      btnType="tertiary"
                      type="button"
                      selected={isSelectAll}
                      grow
                      onClick={() =>
                        isSelectAll
                          ? handleDeselectAll(setValues)
                          : handleSelectAll(setValues)
                      }
                    >
                      {isSelectAll ? 'Deselect All' : 'Select All'}
                    </Button>
                  </Box>
                  <AssignDataSourcesDropdown
                    handleAssign={handleAssign}
                    values={values}
                    resetForm={resetForm}
                    roles={roles}
                    isDisabled={values.dataSources.length === 0}
                  />
                </Box>

                <Wrapper>
                  {dataSources.map(d => (
                    <DataSourceCard
                      dataSource={d}
                      key={d.name}
                      handleCheckbox={handleCheckbox}
                      arrayHelpers={arrayHelpers}
                      values={values}
                    />
                  ))}
                </Wrapper>
              </div>
            )}
          </FieldArray>
        </Form>
      )}
    </Formik>
  );
};

export default DataSourcesForm;
